/**
 * "O que fazer agora" dos disparos: sugestoes derivadas dos numeros, em
 * ordem de urgencia, cada uma com UMA acao.
 *
 * Decisoes nao obvias:
 *  1. Todo insight diz de onde veio (o numero que o justifica) e o que fazer
 *     -- "taxa baixa" sem acao e so ansiedade.
 *  2. OPORTUNIDADE e sempre um publico que JA passou pelo fato (abandonou,
 *     venceu, respondeu sem fechar) e esta sem envio no periodo. Nunca se
 *     sugere falar com quem ainda nao saiu: sem o fato nao ha comissao.
 *  3. As acoes sao dados (`AcaoInsight`), nao callbacks: quem sabe navegar,
 *     retomar um envio ou preparar o formulario e a pagina.
 *  4. Teto de itens: quatro na tela da empresa, cinco no hub. Uma lista de
 *     doze "urgentes" nao tem urgente nenhum.
 */

import type { BadgeTone, IconName } from '../../components/ui';
import { formatBRLFromCents, formatCount, formatPercentFromBps } from '../../lib/format';
import type { CompanySummary } from '../../types';
import { custoDoEnvio, razaoBps } from './comparar';
import type { Broadcast, BroadcastsData, EmailCampaignsData, NumberHealth, Opportunity, SegmentKey, WhatsAppContext } from './types';
import { SEGMENT_LABEL } from './vocabulario';

export type AcaoInsight =
  | { readonly kind: 'retomar'; readonly broadcastId: string; readonly rotulo: string }
  | { readonly kind: 'preparar'; readonly companyId: string; readonly segmentKey: SegmentKey; readonly rotulo: string }
  | { readonly kind: 'ancora'; readonly id: string; readonly rotulo: string }
  | { readonly kind: 'rota'; readonly rota: string; readonly rotulo: string }
  | { readonly kind: 'abrir_empresa'; readonly companyId: string; readonly rotulo: string };

export interface InsightDisparo {
  readonly id: string;
  readonly tone: BadgeTone;
  readonly icone: IconName;
  readonly titulo: string;
  readonly texto: string;
  readonly acao: AcaoInsight | null;
}

const LIMITE_OPTOUT_BPS = 300;
const DIA_MS = 86_400_000;

function restanteDoEnvio(e: Broadcast): number {
  return Math.max(0, e.totalContacts - e.sentCount - e.failedCount - e.optOutCount);
}

function nomeDe(companies: readonly CompanySummary[], id: string): string {
  return companies.find((c) => c.id === id)?.name ?? 'Empresa';
}

/** Melhor oportunidade: maior retorno esperado; sem historico, maior valor parado. */
function melhorOportunidade(lista: readonly Opportunity[]): Opportunity | null {
  const semEnvio = lista.filter((o) => o.lastBroadcastAt === null && o.senderReady);
  const ordenada = [...semEnvio].sort((a, b) => (b.expectedRecoveredCents ?? 0) - (a.expectedRecoveredCents ?? 0) || b.stalledAmountCents - a.stalledAmountCents);
  return ordenada[0] ?? null;
}

export function insightsDoWhatsApp(entrada: {
  readonly company: CompanySummary;
  readonly contexto: WhatsAppContext;
  readonly envios: readonly Broadcast[];
  readonly oportunidades: readonly Opportunity[];
  readonly agora: Date;
}): readonly InsightDisparo[] {
  const { company, contexto, envios } = entrada;
  const saida: InsightDisparo[] = [];

  const conectado = contexto.senders.some((s) => s.connected);
  if (!conectado) {
    saida.push({
      id: 'sem-numero',
      tone: 'critical',
      icone: 'whatsapp',
      titulo: `${company.name} está sem número de saída conectado`,
      texto: 'Sem o WhatsApp do comercial conectado, nenhum envio sai e nenhuma resposta chega às vendedoras.',
      acao: { kind: 'rota', rota: `/admin/empresas/${company.id}`, rotulo: 'Abrir cadastro' },
    });
  }

  const pausadas = envios.filter((e) => e.status === 'pausada' && restanteDoEnvio(e) > 0);
  const pausadaPorOptOut = pausadas.find((e) => e.pausedReason === 'optout_alto');
  if (pausadaPorOptOut !== undefined) {
    const taxa = razaoBps(pausadaPorOptOut.optOutCount, pausadaPorOptOut.sentCount + pausadaPorOptOut.optOutCount);
    saida.push({
      id: `optout-${pausadaPorOptOut.id}`,
      tone: 'critical',
      icone: 'escudo',
      titulo: `"${pausadaPorOptOut.name}" parou sozinho: opt-out em ${taxa === null ? '—' : formatPercentFromBps(taxa)}`,
      texto: 'A trava protegeu o número do comercial. Revise o texto ou o público antes de retomar; retomar desliga a trava deste envio.',
      acao: { kind: 'ancora', id: 'envios', rotulo: 'Ver o envio' },
    });
  }
  const pausadaManual = pausadas.find((e) => e.pausedReason !== 'optout_alto');
  if (pausadaManual !== undefined) {
    saida.push({
      id: `retomar-${pausadaManual.id}`,
      tone: 'warning',
      icone: 'pausa',
      titulo: `"${pausadaManual.name}" está pausado com ${formatCount(restanteDoEnvio(pausadaManual))} contatos por receber`,
      texto: 'Quem abandonou esfria a cada dia: um público de fato recente rende mais hoje do que semana que vem.',
      acao: { kind: 'retomar', broadcastId: pausadaManual.id, rotulo: 'Retomar agora' },
    });
  }

  const principal = contexto.senders.find((s) => s.connected) ?? null;
  const saude: NumberHealth | null = principal === null ? null : contexto.health.find((h) => h.senderId === principal.id) ?? null;
  if (saude !== null && saude.optOutRate7dBps >= LIMITE_OPTOUT_BPS) {
    saida.push({
      id: 'saude-numero',
      tone: 'warning',
      icone: 'escudo',
      titulo: `Opt-out do número em ${formatPercentFromBps(saude.optOutRate7dBps)} nos últimos 7 dias`,
      texto: 'Acima de 3% o WhatsApp começa a limitar o número. No próximo envio: ritmo de 15 por minuto, modelo utility e o público mais quente.',
      acao: { kind: 'ancora', id: 'protecao-do-numero', rotulo: 'Ver proteção' },
    });
  }

  const oportunidade = melhorOportunidade(entrada.oportunidades);
  if (oportunidade !== null) {
    const retorno = oportunidade.expectedRecoveredCents;
    saida.push({
      id: `oportunidade-${oportunidade.segmentKey}`,
      tone: 'brand',
      icone: 'raio',
      titulo: `${formatCount(oportunidade.contacts)} em "${SEGMENT_LABEL[oportunidade.segmentKey]}" sem envio no período`,
      texto:
        retorno === null
          ? `${formatBRLFromCents(oportunidade.stalledAmountCents)} parados. Ainda sem histórico para estimar o retorno: o primeiro envio cria a base.`
          : `${formatBRLFromCents(oportunidade.stalledAmountCents)} parados; pelo histórico da empresa, um envio agora traz cerca de ${formatBRLFromCents(retorno)}.`,
      acao: { kind: 'preparar', companyId: company.id, segmentKey: oportunidade.segmentKey, rotulo: 'Preparar envio' },
    });
  }

  if (conectado && contexto.sellers.length === 0) {
    saida.push({
      id: 'sem-vendedoras',
      tone: 'warning',
      icone: 'usuarios',
      titulo: 'Nenhuma vendedora com papel comercial',
      texto: 'As respostas dos envios caem no número do comercial sem dono. Cadastre ao menos uma vendedora para a venda assistida ser registrada.',
      acao: { kind: 'rota', rota: `/admin/empresas/${company.id}`, rotulo: 'Cadastrar vendedora' },
    });
  }

  const limite = entrada.agora.getTime() - 7 * DIA_MS;
  const naJanela = envios.filter((e) => e.startedAt !== null && new Date(e.startedAt).getTime() >= limite && e.status !== 'agendada');
  if (naJanela.length > 0 && saida.length < 4) {
    saida.push({
      id: 'janela-aberta',
      tone: 'info',
      icone: 'relogio',
      titulo: `${formatCount(naJanela.length)} ${naJanela.length === 1 ? 'envio ainda está' : 'envios ainda estão'} na janela de 7 dias`,
      texto: 'Pagamentos desses públicos ainda podem entrar e ser creditados ao último toque. O retorno deles vai subir sem nenhuma ação.',
      acao: null,
    });
  }

  return saida.slice(0, 4);
}

export function insightsDoHub(entrada: {
  readonly envios: BroadcastsData;
  readonly emails: EmailCampaignsData | null;
  readonly companies: readonly CompanySummary[];
}): readonly InsightDisparo[] {
  const { envios, emails, companies } = entrada;
  const saida: InsightDisparo[] = [];

  const oportunidade = melhorOportunidade(envios.opportunities);
  if (oportunidade !== null) {
    const retorno = oportunidade.expectedRecoveredCents;
    saida.push({
      id: `oportunidade-${oportunidade.companyId}-${oportunidade.segmentKey}`,
      tone: 'brand',
      icone: 'raio',
      titulo: `${nomeDe(companies, oportunidade.companyId)}: ${formatCount(oportunidade.contacts)} em "${SEGMENT_LABEL[oportunidade.segmentKey]}" sem envio`,
      texto:
        retorno === null
          ? `${formatBRLFromCents(oportunidade.stalledAmountCents)} parados e nenhum envio no período.`
          : `${formatBRLFromCents(oportunidade.stalledAmountCents)} parados; o histórico da empresa aponta cerca de ${formatBRLFromCents(retorno)} de retorno.`,
      acao: { kind: 'preparar', companyId: oportunidade.companyId, segmentKey: oportunidade.segmentKey, rotulo: 'Preparar envio' },
    });
  }

  const travadas = envios.opportunities.filter((o) => !o.senderReady);
  const empresasTravadas = Array.from(new Set(travadas.map((o) => o.companyId)));
  const primeiraTravada = empresasTravadas[0];
  if (primeiraTravada !== undefined) {
    const parado = travadas.filter((o) => o.companyId === primeiraTravada).reduce((acc, o) => acc + o.stalledAmountCents, 0);
    saida.push({
      id: `sem-numero-${primeiraTravada}`,
      tone: 'critical',
      icone: 'whatsapp',
      titulo: `${nomeDe(companies, primeiraTravada)} sem número de saída: ${formatBRLFromCents(parado)} parados sem canal`,
      texto: 'Enquanto o WhatsApp do comercial não estiver conectado, nenhum público dessa empresa pode receber envio.',
      acao: { kind: 'rota', rota: `/admin/empresas/${primeiraTravada}`, rotulo: 'Conectar número' },
    });
  }

  const pausadas = envios.broadcasts.filter((e) => e.status === 'pausada' && restanteDoEnvio(e) > 0);
  if (pausadas.length > 0) {
    const faltam = pausadas.reduce((acc, e) => acc + restanteDoEnvio(e), 0);
    const primeira = pausadas[0];
    saida.push({
      id: 'pausadas',
      tone: 'warning',
      icone: 'pausa',
      titulo: `${formatCount(pausadas.length)} ${pausadas.length === 1 ? 'envio pausado' : 'envios pausados'} com ${formatCount(faltam)} contatos por receber`,
      texto: (() => {
        const pelaTrava = pausadas.filter((e) => e.pausedReason === 'optout_alto').length;
        return pelaTrava === 0
          ? 'Todos pausados pela equipe. Quem abandonou esfria a cada dia: retome ou encerre.'
          : `${formatCount(pelaTrava)} ${pelaTrava === 1 ? 'parou' : 'pararam'} sozinho pela trava de opt-out. Revise o texto ou o público antes de retomar.`;
      })(),
      acao: primeira === undefined ? null : { kind: 'abrir_empresa', companyId: primeira.companyId, rotulo: 'Ver envios' },
    });
  }

  const porEmpresa = new Map<string, { custo: number; comissao: number }>();
  for (const e of envios.broadcasts) {
    const atual = porEmpresa.get(e.companyId) ?? { custo: 0, comissao: 0 };
    porEmpresa.set(e.companyId, { custo: atual.custo + custoDoEnvio(e), comissao: atual.comissao + e.commissionCents });
  }
  const deficitaria = Array.from(porEmpresa.entries()).find(([, v]) => v.custo > 0 && v.custo > v.comissao);
  if (deficitaria !== undefined) {
    const [companyId, v] = deficitaria;
    saida.push({
      id: `deficit-${companyId}`,
      tone: 'warning',
      icone: 'moeda',
      titulo: `${nomeDe(companies, companyId)}: disparos custaram ${formatBRLFromCents(v.custo)} e renderam ${formatBRLFromCents(v.comissao)} de comissão`,
      texto: 'Prefira a Evolution (sem tarifa por mensagem) ou modelos utility para esses públicos.',
      acao: { kind: 'abrir_empresa', companyId, rotulo: 'Abrir envios' },
    });
  }

  if (emails !== null && (emails.overdueScheduled > 0 || emails.providerState === 'a_definir')) {
    saida.push({
      id: 'email-provedor',
      tone: emails.overdueScheduled > 0 ? 'critical' : 'info',
      icone: 'email',
      titulo:
        emails.overdueScheduled > 0
          ? `${formatCount(emails.overdueScheduled)} ${emails.overdueScheduled === 1 ? 'campanha de e-mail passou' : 'campanhas de e-mail passaram'} da hora sem sair`
          : 'Provedor de e-mail ainda a definir',
      texto: 'Enquanto Brevo ou Resend não for escolhido, campanhas e automações de e-mail ficam registradas, mas nada sai.',
      acao: { kind: 'rota', rota: '/admin/disparos/email', rotulo: 'Abrir e-mail' },
    });
  }

  return saida.slice(0, 5);
}
